import { FC, useEffect } from 'react';
import { Column } from 'primereact/column';
import ExportPatientsFilters from 'components/export/ExportPatientsFilters';
import exportPatientsStore from 'store/ExportPatientsStore';
import { observer } from 'mobx-react';
import { Patient } from 'types';
import moment from 'moment/moment';
import {
  AgeBodyTemplate,
  DiabetTypeBodyTemplate,
  GenderBodyTemplate,
  GlucometerSerialNumberBodyTemplate,
  GlucoseRangeBodyTemplate,
  WeightBodyTemplate,
} from 'components/export/ExportPatientsColumnsBodyTemplates';
import { useNavigate } from 'react-router-dom';
import { EXPORT_R00T_ROUTE } from 'routes/paths';
import ActionButtons from 'components/base/action-buttons';
import { DataTable } from 'components/base/DataTable';
import { LoadingStatus, PaginationDirection } from 'enums';
import Spinner from 'components/base/spinner';
import PaginationButtons from 'components/base/PaginationButtons';

const ExportPatientsPage: FC = () => {
  const navigation = useNavigate();
  const patientsData = exportPatientsStore.patients.map((patient: Patient) => ({
    ...patient,
    registrationDate: moment(patient.registrationDate).format(
      'DD-MM-yyyy HH:mm',
    ),
  }));
  useEffect(() => {
    exportPatientsStore.cleanFilter();
    exportPatientsStore.fetchPatients({
      filters: {},
      page: {
        anchorEntity: null,
        fetchDirection: PaginationDirection.FORWARD,
      },
    });
  }, []);

  const toFirstPage = () => {
    const { filters } = exportPatientsStore;

    exportPatientsStore.fetchPatients({
      filters,
      page: {
        fetchDirection: PaginationDirection.FORWARD,
        anchorEntity: null,
      },
    });
  };

  const toNextPage = () => {
    const { filters } = exportPatientsStore;

    exportPatientsStore.fetchPatients({
      filters,
      page: {
        fetchDirection: PaginationDirection.FORWARD,
        anchorEntity:
          exportPatientsStore.patients[exportPatientsStore.patients.length - 1],
      },
    });
  };

  const toLastPage = () => {
    const { filters } = exportPatientsStore;

    exportPatientsStore.fetchPatients({
      filters,
      page: {
        fetchDirection: PaginationDirection.BACKWARD,
        anchorEntity: null,
      },
    });
  };

  const toPreviousPage = () => {
    const { filters } = exportPatientsStore;

    exportPatientsStore.fetchPatients({
      filters,
      page: {
        fetchDirection: PaginationDirection.BACKWARD,
        anchorEntity: exportPatientsStore.patients[0],
      },
    });
  };

  const handleClickExcel = () => {
    exportPatientsStore.fetchPatientsExcel();
  };

  return (
    <>
      <h1 className="mb-5">Пациенты</h1>
      <div className="mb-4">
        <ActionButtons
          textButtonSuccess="Выгрузить excel"
          onClickSuccessButton={handleClickExcel}
          onClickBackButton={() => navigation(EXPORT_R00T_ROUTE)}
        />
      </div>
      <ExportPatientsFilters />
      {exportPatientsStore.status === LoadingStatus.DONE ? (
        <>
          <DataTable
            value={patientsData}
            rows={10}
            responsiveLayout="stack"
          >
            <Column
              field="login"
              header="Логин"
            />
            <Column
              field="gender"
              header="Пол"
              body={GenderBodyTemplate}
            />
            <Column
              field="diabetType"
              header="Тип диабета"
              body={DiabetTypeBodyTemplate}
            />
            <Column
              field="age"
              header="Возраст"
              body={AgeBodyTemplate}
            />
            <Column
              field="weight"
              header="Вес"
              body={WeightBodyTemplate}
            />
            <Column
              field="glucoseRange"
              header="Диапазон глюкозы"
              body={GlucoseRangeBodyTemplate}
            />
            <Column
              field="registrationDate"
              header="Дата регистрации"
            />
            <Column
              field="glucometerSerialNumber"
              header="Серийный номер глюкометра"
              body={GlucometerSerialNumberBodyTemplate}
            />
          </DataTable>
          <PaginationButtons
            isDisablePrevButtons={!exportPatientsStore.hasPrevious}
            isDisableNextButtons={!exportPatientsStore.hasNext}
            toFirstPage={toFirstPage}
            toNextPage={toNextPage}
            toLastPage={toLastPage}
            toPreviousPage={toPreviousPage}
          />
        </>
      ) : (
        <Spinner />
      )}
    </>
  );
};

const ExportPatientsPageObserver = observer(ExportPatientsPage);

export { ExportPatientsPageObserver as ExportPatientsPage };
